import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Loader2, Activity, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
	intelligenceApi,
	type IntelligencePeriodPreset,
} from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import {
	CommandCentreFilters,
	type IntelligencePeriodFilterValue,
} from "@/components/intelligence/CommandCentreFilters";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import {
	OperationsMetricCard,
	UnavailableOperationsCard,
} from "@/components/intelligence/OperationsMetricCard";
import { WardOccupancyTable } from "@/components/intelligence/WardOccupancyTable";
import { OperationsTrendChart } from "@/components/intelligence/OperationsTrendChart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const PRIMARY_METRIC_KEYS = [
	"opd_visits",
	"ipd_admissions",
	"ipd_discharges",
	"current_inpatients",
	"occupancy_pct",
	"average_los_days",
];

export default function OperationsIntelligencePage() {
	const { user, activeHospitalId, isChainAdmin } = useAuth();
	const isSuperAdmin = user?.role === "SUPER_ADMIN";
	const isInvestor = user?.role === "INVESTOR";
	const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
	const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
	const chainHospitals = user?.hospitals ?? [];
	const isChainScoped = isChainAdmin || investorHasChainScope;

	const defaultFinancialHospitalId = useMemo(
		() =>
			defaultFinancialReportHospitalId({
				isSuperAdmin,
				isChainScoped,
				activeHospitalId,
				fallbackHospitalId: chainHospitals[0]?.id ?? null,
			}),
		[isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
	);

	const [period, setPeriod] = useState<IntelligencePeriodFilterValue>({ preset: "this_month" });
	const [selectedHospitalId, setSelectedHospitalId] = useState("");

	useEffect(() => {
		if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
			setSelectedHospitalId(defaultFinancialHospitalId || "all");
		}
	}, [defaultFinancialHospitalId, selectedHospitalId]);

	const { data: superHospitals } = useQuery({
		queryKey: ["super-admin-hospitals-operations"],
		queryFn: () => superAdminApi.getHospitals(),
		enabled: isSuperAdmin,
	});

	const hospitalOptions = useMemo(() => {
		if (isSuperAdmin && superHospitals?.length) {
			return superHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (isChainScoped && chainHospitals.length) {
			return chainHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (user?.hospitalId) {
			const name = chainHospitals.find((h) => h.id === user.hospitalId)?.name ?? "My Hospital";
			return [{ id: user.hospitalId, name }];
		}
		return [];
	}, [isSuperAdmin, isChainScoped, superHospitals, chainHospitals, user?.hospitalId]);

	const reportHospitalId = useMemo(
		() =>
			intelligenceReportHospitalId({
				showHospitalFilter,
				selectedHospitalId,
				isChainScoped,
			}),
		[showHospitalFilter, selectedHospitalId, isChainScoped]
	);

	const customReady =
		period.preset !== "custom" || (!!period.from && !!period.to && period.from <= period.to);

	const queryParams = {
		period: period.preset as IntelligencePeriodPreset,
		from: period.preset === "custom" ? period.from : undefined,
		to: period.preset === "custom" ? period.to : undefined,
		hospitalId: reportHospitalId,
		compare: true,
	};

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["intelligence-operations", queryParams],
		queryFn: () => intelligenceApi.getOperations(queryParams),
		enabled: customReady,
	});

	const primaryMetrics = useMemo(() => {
		if (!data) return [];
		return PRIMARY_METRIC_KEYS.map((key) => data.metrics.find((m) => m.key === key)).filter(
			(m): m is NonNullable<typeof m> => !!m
		);
	}, [data]);

	return (
		<div className="space-y-6 pb-10">
			<IntelligenceSubNav basePath={isSuperAdmin ? "/super-admin" : "/hospital-admin"} />

			<div>
				<div className="flex items-center gap-2 text-indigo-700">
					<Activity className="h-6 w-6" />
					<h1 className="text-2xl font-bold tracking-tight text-slate-900">
						Operations Intelligence
					</h1>
				</div>
				<p className="text-sm text-muted-foreground mt-1">
					Bed occupancy, IPD census, and length of stay · {data?.period.label ?? "—"}
				</p>
			</div>

			<CommandCentreFilters
				hospitals={hospitalOptions}
				showHospitalFilter={showHospitalFilter}
				hospitalId={selectedHospitalId}
				onHospitalChange={setSelectedHospitalId}
				period={period}
				onPeriodChange={setPeriod}
			/>

			{data && customReady && (
				<div className="flex justify-end">
					<IntelligenceServerExportButton
						report="operations"
						params={queryParams}
						filename={`operations-intelligence-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
					/>
				</div>
			)}

			{isLoading && customReady && (
				<div className="flex justify-center py-16 text-muted-foreground gap-2">
					<Loader2 className="h-5 w-5 animate-spin" /> Loading…
				</div>
			)}

			{isError && customReady && (
				<Card className="border-destructive/40">
					<CardContent className="pt-4 text-sm text-destructive flex gap-2">
						<AlertTriangle className="h-4 w-4 shrink-0" />
						{(error as Error)?.message ?? "Failed to load operations intelligence"}
					</CardContent>
				</Card>
			)}

			{data && customReady && (
				<>
					{data.limitations[0] && (
						<p className="text-xs text-muted-foreground">{data.limitations[0]}</p>
					)}

					{data.statements.length > 0 && (
						<Card>
							<CardContent className="pt-4">
								<ul className="space-y-1 text-sm text-slate-700">
									{data.statements.map((s) => (
										<li key={s}>• {s}</li>
									))}
								</ul>
							</CardContent>
						</Card>
					)}

					<div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
						{primaryMetrics.map((m) => (
							<OperationsMetricCard
								key={m.key}
								metric={m}
								comparisonLabel={data.comparisonPeriod?.label}
							/>
						))}
						{data.unavailable.map((u) => (
							<UnavailableOperationsCard key={u.key} metric={u} />
						))}
					</div>

					<OperationsTrendChart dailyTrend={data.dailyTrend} />

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">
								Ward occupancy (as of period end)
							</CardTitle>
							<p className="text-xs text-muted-foreground">
								{data.bedOccupancy.summary.totalOccupied} of {data.bedOccupancy.summary.totalBeds}{" "}
								beds occupied ({data.bedOccupancy.summary.occupancyPercent}%)
							</p>
						</CardHeader>
						<CardContent>
							<WardOccupancyTable wards={data.wardOccupancy} />
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}
